// src/components/PatientPortal/PatientPortalManagement.jsx
import React, { useState, useEffect, useCallback } from "react";
import { Row, Col, Space, Input, Button, DatePicker, Spin, message } from "antd";
import { SearchOutlined, ReloadOutlined } from "@ant-design/icons";
import InviteList from "./InviteList/InviteList";
import { fetchInvites } from "./utils/api";
import dayjs from "dayjs";
import { useAuth } from "../../auth/AuthContext";

const { RangePicker } = DatePicker;

const PatientPortalManagement = () => {
  const auth = useAuth();
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [emailQuery, setEmailQuery] = useState("");
  const [dateRange, setDateRange] = useState(null);
  const [showRevoked, setShowRevoked] = useState(true);
  const [showUsed, setShowUsed] = useState(true);

  const loadInvites = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchInvites({
        page,
        pageSize,
        emailQuery,
        dateRange,
        token: auth.user["access_token"],
      });

      const filtered = data.data
        .filter((i) => (showRevoked ? true : !i.revoked))
        .filter((i) => (showUsed ? true : !i.used));

      setInvites(filtered);
      setTotal(data.count ?? filtered.length);
    } catch (err) {
      console.error(err);
      message.error("Failed to load invites");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, emailQuery, dateRange, showRevoked, showUsed, auth.user]);

  useEffect(() => {
    loadInvites();
  }, [loadInvites]);

  const exportCsv = () => {
    if (!invites.length) {
      message.info("No invites to export");
      return;
    }
    const rows = [
      ["invite_id", "patient_id", "email", "created_at", "expires_at", "revoked", "used"],
      ...invites.map((i) => [
        i.invite_id,
        i.patient_id,
        i.email,
        i.created_at,
        i.expires_at,
        i.revoked,
        i.used,
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "")}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `invites_${dayjs().format("YYYYMMDD_HHmmss")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ padding: 16 }}>
      {/* Filters */}
      <Row gutter={[12, 12]} align="middle" justify="space-between" style={{ marginBottom: 12 }}>
        <Col>
          <Space>
            <Input
              placeholder="Search email..."
              value={emailQuery}
              onChange={(e) => {
                setEmailQuery(e.target.value);
                setPage(1);
              }}
              prefix={<SearchOutlined />}
              allowClear
              style={{ width: 260 }}
            />
            <RangePicker
              onChange={(vals) => setDateRange(vals ? [vals[0].toDate(), vals[1].toDate()] : null)}
            />
            <Button
              onClick={() => {
                setEmailQuery("");
                setDateRange(null);
                setShowRevoked(true);
                setShowUsed(true);
                setPage(1);
              }}
            >
              <ReloadOutlined /> Reset
            </Button>
            <Button onClick={exportCsv}>Export CSV</Button>
          </Space>
        </Col>
        <Col>
          <Space>
            <label>
              <input
                type="checkbox"
                checked={showRevoked}
                onChange={(e) => setShowRevoked(e.target.checked)}
              />{" "}
              Show Revoked
            </label>
            <label>
              <input
                type="checkbox"
                checked={showUsed}
                onChange={(e) => setShowUsed(e.target.checked)}
              />{" "}
              Show Used
            </label>
          </Space>
        </Col>
      </Row>

      {/* Invite list */}
      <Spin spinning={loading || pageLoading}>
        <InviteList
          invites={invites}
          page={page}
          pageSize={pageSize}
          total={total}
          setPage={setPage}
          setPageSize={setPageSize}
          reload={loadInvites}
          setPageLoading={setPageLoading}
        />
      </Spin>
    </div>
  );
};

export default PatientPortalManagement;
